import type { CalculatorContent, Faq } from '@/types/calculator'
import type { CarAffordabilityInputs } from './types'

export const content: CalculatorContent<CarAffordabilityInputs> = {
  intro:
    'How much car can I afford? This car affordability calculator for South Africa adds up the finance instalment, insurance, fuel and maintenance, then compares the true monthly ownership cost to your income.',
  howItWorks: [
    'The finance amount is the vehicle price minus your deposit or trade-in. A balloon (residual) percentage, if any, is a lump sum left owing at the end of the term.',
    'The monthly instalment uses the amortising-loan formula with the balloon discounted to present value — a balloon lowers the instalment but increases the total finance cost.',
    'Total monthly ownership cost adds insurance, fuel and maintenance to the instalment, because the true cost of a car is far more than the repayment alone.',
    'The affordability signal compares total ownership cost to gross monthly income: above roughly a quarter flags "maybe", above about a third flags "no".',
  ],
  formulas: [
    {
      label: 'Finance amount',
      expression: 'vehicle price − deposit',
    },
    {
      label: 'Monthly instalment',
      expression: '(finance amount − balloon PV) × r ÷ (1 − (1 + r)⁻ⁿ)',
      notes: 'r = annual rate ÷ 12, n = term in months, balloon PV = balloon ÷ (1 + r)ⁿ.',
    },
    {
      label: 'Total monthly ownership cost',
      expression: 'instalment + insurance + fuel + maintenance',
    },
    {
      label: 'Ownership cost to income',
      expression: 'total monthly ownership cost ÷ gross monthly income',
    },
  ],
  example: {
    title: 'A R300 000 car on a R35 000 monthly income',
    description:
      'A buyer puts down a deposit on a hatchback financed over six years and checks whether the all-in monthly cost is affordable.',
    inputs: {
      monthlyIncome: 35_000,
      deposit: 50_000,
      vehiclePrice: 300_000,
      interestRate: 0.13,
      termYears: 6,
      balloonPercent: 0,
      insuranceMonthly: 1_500,
      fuelMonthly: 3_000,
      maintenanceMonthly: 1_000,
    },
  },
  definitions: [
    {
      term: 'Balloon / residual',
      description:
        'A percentage of the finance amount deferred to a final lump-sum payment, lowering monthly instalments but increasing total cost.',
    },
    {
      term: 'Instalment',
      description: 'The fixed monthly repayment on the vehicle finance agreement.',
    },
    {
      term: 'Total cost of ownership',
      description:
        'Instalment plus insurance, fuel, maintenance and licensing — the real monthly cost of running the car.',
    },
    {
      term: 'Affordability ratio',
      description:
        'Total monthly car cost divided by gross income. Keeping it below about a quarter is a common guideline.',
    },
  ],
}

export const faqs: Faq[] = [
  {
    question: 'What is balloon / residual finance?',
    answer:
      'A balloon payment is a lump sum still owed at the end of the finance term. It lowers monthly instalments but means you must refinance or pay the amount at the end.',
  },
  {
    question: 'How much car can I afford on my salary?',
    answer:
      'A common guideline keeps total car costs — instalment, insurance, fuel and maintenance — below about a quarter to a third of gross income. Enter your figures to see the signal.',
  },
  {
    question: 'Are insurance and fuel included?',
    answer:
      'You enter monthly estimates for insurance, fuel and maintenance, and they are added to the instalment in the total ownership cost.',
  },
  {
    question: 'What fees are excluded?',
    answer:
      'Finance initiation, monthly admin and licensing fees are not included — add a margin to your figures to cover them.',
  },
]
